import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { Alert, Box, Stack } from '@mui/material'
import { ActionsMenu } from '@/components/table/actions'
import { ActionCopyJSONItem } from '@/components/table/actions/ActionCopyJSONItem/ActionCopyJSONItem'
import { FilterableTable } from '@/components/table/base'
import { STORE_STATUS } from '@/constants/store'
import { ROUTE_PARAM, ROUTE_URL } from '@/router/constants'
import { useLeaderboardStore } from '@/stores/golfPricing/leaderboard.store'
import type { LeaderboardTableRow } from '@/types/dde/golfLeaderboard.types'

import { DDE_TABLE_COLUMNS, DDE_TABLE_FILTERS } from './LeaderboardView.dde.table'

const tableId = 'dde-golf-tournament-leaderboard-table'

export const LeaderboardViewDDE = () => {
  const navigate = useNavigate()
  const { [ROUTE_PARAM.TOURNAMENT_ID]: tournamentIdParam } = useParams()

  const { data, status, error, wsError, isConnecting } = useLeaderboardStore(
    useShallow((state) => ({
      data: state.data,
      status: state.status,
      error: state.error,
      wsError: state.wsError,
      isConnecting: state.isConnecting
    }))
  )

  useEffect(() => {
    const tournamentId = tournamentIdParam ? Number(tournamentIdParam) : null
    if (tournamentId && !Number.isNaN(tournamentId)) {
      useLeaderboardStore.getState().connectWebSocket(tournamentId)
    }
  }, [tournamentIdParam])

  const isLoading = isConnecting && data.length === 0
  const isFetching = isConnecting && data.length > 0

  const displayError = wsError ?? (status === STORE_STATUS.error ? error : null)
  const shouldShowError = displayError && !isConnecting && data.length === 0

  if (shouldShowError) {
    if (displayError === 'WebSocket connection error') {
      return (
        <Stack
          fontSize={16}
          flexDirection="row"
          alignItems="center"
          gap={0.5}
          paddingY={3}
          width="100%"
          justifyContent="center"
        >
          No data due to WebSocket Connection error
        </Stack>
      )
    }
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load leaderboard: {displayError}</Alert>
      </Box>
    )
  }

  return (
    <FilterableTable
      data={data}
      tableId={tableId}
      filters={DDE_TABLE_FILTERS}
      columns={DDE_TABLE_COLUMNS}
      loading={isLoading}
      fetching={isFetching}
      clientSideSortAndPagination
      clientSideFilters
      onRowClick={(row) =>
        navigate(
          ROUTE_URL.GOLF_PRICING.TOURNAMENT_LEADERBOARD.TEAM.replace(
            `:${ROUTE_PARAM.TOURNAMENT_ID}`,
            String(row.tournamentId)
          ).replace(`:${ROUTE_PARAM.TEAM_NO}`, String(row.teamNo))
        )
      }
      rowActions={
        <ActionsMenu>
          <ActionCopyJSONItem
            getValueToCopy={(row: LeaderboardTableRow) => JSON.stringify(row.rawStanding, null, 2)}
          />
        </ActionsMenu>
      }
    />
  )
}
